import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import { withPathParams } from '@helpcenter/with-path-params';
import type { RouteContext } from 'react-resource-router';
import type { State } from 'state';
import { fetchPortalAction } from 'state/actions/portal';

import { fetchRequestDetailsModel, changeSubscription } from 'state/actions/request-details';
import { getPortalNameFromBranding } from 'state/selectors/branding';
import { getRequestSpecificConnectAddonPanels } from 'state/selectors/connect-addon';
import { getCanAdministerProject, getName, getPortal } from 'state/selectors/portal';
import { getRequestDetails, getError } from 'state/selectors/request-details';
import { isAtlassianAccountManaged } from 'state/selectors/user';
import { getIssueAnalyticsContext } from '@atlassian/help-center-common-util/analytics/issue-context';
import { toNumber } from '@atlassian/help-center-common-util/number';
import RequestDetailsComponent from './request-details';

type ExternalProps = Omit<RouteContext, 'action'>;

const selector = createSelector(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (state: State, props: ExternalProps) => getPortal(state, toNumber(props.match.params.portalId!)),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (state: State, props: ExternalProps) => getRequestDetails(state, props.match.params.requestKey!),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (state: State, props: ExternalProps) => getError(state, props.match.params.requestKey!),
    (state: State, props: ExternalProps) => {
        return getName(state, Number(props.match.params.portalId)) ?? getPortalNameFromBranding(state);
    },
    (state: State, props: ExternalProps) => getIssueAnalyticsContext(state, Number(props.match.params.portalId)),
    (state: State) => isAtlassianAccountManaged(state),
    (state: State, props: ExternalProps) =>
        getRequestSpecificConnectAddonPanels({
            state,
            location: 'detailsPanels',
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            requestKey: props.match.params.requestKey!,
        }),
    (state: State, props: ExternalProps) => {
        return Boolean(getCanAdministerProject(state, Number(props.match.params.portalId)));
    },
    (
        portal,
        requestDetails,
        persistedError,
        portalName,
        issueAnalyticsContext,
        isManagedAccount,
        connectPanels,
        isProjectAdmin
    ) => ({
        portal,
        requestDetails,
        issueAnalyticsContext,
        portalName,
        isProjectAdmin,
        isAtlassianManagedAccount: isManagedAccount,
        ...persistedError,
        hasConnectPanels: connectPanels && connectPanels.length > 0,
        isLoading: requestDetails === undefined && persistedError === undefined,
    })
);

export default connect(selector, {
    fetchPortal: fetchPortalAction,
    fetchRequestDetails: fetchRequestDetailsModel,
    changeSubscriptionAction: changeSubscription,
})(withPathParams(RequestDetailsComponent));
